import * as React from "react";
import { OTPInput, OTPInputContext } from "input-otp";
import { cn } from "./ui/utils";

interface PinInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
}

function PinSlot({ index }: { index: number }) {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext?.slots[index] ?? {};

  return (
    <div
      data-active={isActive}
      className={cn(
        "relative flex h-12 w-11 items-center justify-center rounded-md border border-gray-300 bg-white text-xl font-semibold text-gray-900 shadow-sm transition-all outline-none",
        "data-[active=true]:border-primary data-[active=true]:ring-2 data-[active=true]:ring-primary/20 data-[active=true]:z-10"
      )}
    >
      {/* PINs are masked — show a bullet instead of the digit */}
      {char ? "•" : null}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink bg-foreground h-4 w-px duration-1000" />
        </div>
      )}
    </div>
  );
}

/**
 * Masked numeric PIN entry built on input-otp (same primitive as the shadcn
 * InputOTP, but renders bullets). `inputMode="numeric"` brings up the
 * numeric keypad on phones/tablets.
 */
export function PinInput({ value, onChange, onComplete, length = 6, disabled }: PinInputProps) {
  return (
    <OTPInput
      maxLength={length}
      value={value}
      onChange={onChange}
      onComplete={onComplete}
      disabled={disabled}
      inputMode="numeric"
      pattern="^[0-9]*$"
      containerClassName="flex items-center justify-center gap-2 has-disabled:opacity-50"
    >
      <div className="flex items-center gap-2">
        {Array.from({ length }, (_, i) => (
          <PinSlot key={i} index={i} />
        ))}
      </div>
    </OTPInput>
  );
}
